import { useEffect, useRef, type MouseEvent } from 'react';
import { on } from '../viewport/events.ts';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { mmPerPxAt, scaleBar } from './scaleBar.ts';
import { MotionMeter } from './motionFps.ts';
import { drawnOverlays, mainSlot, slotsOf } from '../app/scene.ts';
import { brainMaps, simsNote } from '../viewport/bridge.ts';
import { cameraAxes, gnomonSvg } from './gnomon.ts';
import type { Preset } from '../viewport/api.ts';
import type { PharmaHeader } from '../data/sims/types.ts';
import { TARGET_LABEL } from './Traces.tsx';
import { scenarioById } from '../scenarios/index.ts';
import { previewNote, previewWhy } from './preview.ts';

const px = (name: string) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;

/** Orientation gnomon (`hud.orientation`), redrawn on camera events. Colourless on purpose: red
 *  already means artery in the picture. */
function Orientation() {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const size = px('--ba-size-hud-orientation') || 64;
    const letter = px('--ba-hud-orientation-letters-size') || 10;
    const svg = ref.current;
    svg?.setAttribute('viewBox', `${-size / 2} ${-size / 2} ${size} ${size}`);
    return on('camera', (e) => {
      if (ref.current) ref.current.innerHTML = gnomonSvg(cameraAxes(e.viewProj), size, letter);
    });
  }, []);
  // Clicking a letter gives that view, framing the isolation if there is one, as a Views row does.
  const onClick = (e: MouseEvent<SVGSVGElement>) => {
    const view = (e.target as Element).closest('[data-view]')?.getAttribute('data-view') as Preset | null;
    if (!view) return;
    const s = useAtlas.getState();
    s.camera({ preset: view, ...(s.isolate ? { frame: s.isolate, direction: view } : {}) });
  };
  return <svg ref={ref} className="orientation" aria-label="Orientation: R, A and S as the camera sees them; click a letter for that view" onClick={onClick} />;
}

/** The length of a bar at the orbit target's depth, updated on camera events; full while the
 *  camera moves, 60 % from 600 ms after it settles. */
function ScaleBar() {
  const root = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const text = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const lo = px('--ba-size-hud-scalebar-min') || 80;
    const hi = px('--ba-size-hud-scalebar-max') || 200;
    let rest: ReturnType<typeof setTimeout> | undefined;
    const off = on('camera', (e) => {
      if (!root.current || !bar.current || !text.current) return;
      const b = scaleBar(mmPerPxAt(e.viewProj, e.eyeMm, e.orbit.targetMm, e.width, e.height), lo, hi);
      bar.current.style.width = `${b.px.toFixed(1)}px`;
      text.current.textContent = b.text;
      root.current.dataset.rest = 'false';
      clearTimeout(rest);
      rest = setTimeout(() => {
        if (root.current) root.current.dataset.rest = 'true';
      }, 600);
    });
    return () => {
      off();
      clearTimeout(rest);
    };
  }, []);
  return (
    <div ref={root} className="scalebar" data-rest="true" aria-label="Scale at the focus depth">
      <span ref={text} className="scalebar-text ba-type-hud-caption num" />
      <div ref={bar} className="scalebar-bar" />
    </div>
  );
}

// Frame timing in full (fps · CPU ms per frame) is a developer switch.
const SHOW_STATS = new URLSearchParams(location.search).has('stats');

/**
 * Why a pharma run's cortex map isn't drawn, when it has one. No engine reads pharma-sim-v0 yet and
 * Schaefer parcels aren't structures in a pack, so the map draws by DKT region once the HD cortex
 * has its `dkt` layer. A target the run doesn't map says so; a run with no engagement map (a
 * comparison of PET atlases) needs no note.
 */
function pharmaMapNote(s: AtlasState): string | null {
  const main = mainSlot(s);
  if (main?.format !== 'pharma' || main.appOnly) return null;
  const own = s.overlays.filter((o) => o.id.startsWith(`${main.slot}:`));
  if (own.length) return drawnOverlays(s).some((o) => own.includes(o)) ? null : 'Cortex map: waits for the HD cortex’s DKT layer';
  const h = main.header as PharmaHeader;
  const target = s.traceView.target && h.target_codes?.includes(s.traceView.target) ? s.traceView.target : h.primary_target;
  return target && h.spatial_target_codes?.length && !h.spatial_target_codes.includes(target) ? `${TARGET_LABEL(target)}: no cortex map in this run` : null;
}

// One meter for the session, so the caption keeps its reading across remounts.
const meter = new MotionMeter();

/** The fps of the last motion, written straight into the caption: camera events come every frame. */
function MotionFps() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const show = () => {
      if (!ref.current) return;
      ref.current.textContent = meter.fps === null ? '' : `${meter.fps}\u202Ffps`;
      ref.current.hidden = meter.fps === null;
    };
    show();
    const offCamera = on('camera', () => meter.frame(performance.now()) && show());
    const offStats = on('stats', (e) => useAtlas.getState().playing && meter.playing(e.fps) && show());
    return () => {
      offCamera();
      offStats();
    };
  }, []);
  return <div ref={ref} className="engine-status ba-type-caption num" />;
}

/** The engine state caption (spec §10), left of the scale bar, 4 px off the corner's foot as the sheet
 *  sets it, and hidden in presentation unless the viewport has stopped. */
function EngineStatus() {
  const engine = useAtlas((s) => s.engine);
  const load = useAtlas((s) => s.load);
  const reading = useAtlas((s) => s.reading);
  const slots = useAtlas(slotsOf);
  const space = useAtlas((s) => s.catalogue?.pack.space);
  const overlays = useAtlas(drawnOverlays);
  const mapNote = useAtlas(pharmaMapNote);
  const loadMaps = useAtlas((s) => s.load.maps);
  const shownMaps = useAtlas((s) => s.brain?.maps);
  // A view whose own pack didn't load: the scene went back to its pack, and says so here (never a toast).
  const viewFailed = useAtlas((s) => s.viewFailed);
  const scenario = useAtlas((s) => s.scenario);
  const presenting = useAtlas((s) => s.presenting);
  const failed = viewFailed ? { name: scenarioById(scenario).presets.find((p) => p.pack === viewFailed.pack)?.name ?? viewFailed.pack, why: viewFailed.why } : null;
  const caption = (text: string, error = false, title?: string) => (
    <div className={`engine-status ba-type-caption ${error ? 'error' : ''}`} title={title ?? text}>
      {text}
    </div>
  );
  if (engine.status === 'error') return caption(engine.error ?? 'The viewport stopped', true);
  // Presentation keeps the picture, its Methods caption, the scale bar and the gnomon (spec §10).
  if (presenting) return null;
  if (load.status === 'loading') return caption(load.total ? `Loading ${load.loaded ?? 0} of ${load.total}` : 'Loading…');
  if (load.status === 'error') return caption(load.error ?? 'Couldn’t load the pack', true);
  // A section's first cut reads the pack's volumes (the viewport's load doesn't since #565).
  if (reading) return caption(`Reading the section’s volumes, ${reading.loaded} of ${reading.total}`);
  if (failed) return caption(`${failed.name} didn’t load in this viewport: ${failed.why}`, false, failed.why);
  if (engine.kind === 'mock') return engine.fallback ? caption(previewNote(engine.fallback), false, engine.fallback.detail ?? previewWhy(engine.fallback)) : caption('Mock viewport');
  const maps = brainMaps(loadMaps, shownMaps, engine.sims, engine.maps);
  const note = simsNote(engine.api, slots, space, { sims: engine.sims, refused: engine.undrawn, overlays, ...(maps ? { maps } : {}) });
  if (note) return caption(note);
  if (mapNote) return caption(mapNote);
  if (SHOW_STATS && engine.fps !== undefined) return caption(`${Math.round(engine.fps)}\u202Ffps · ${(engine.frameMs ?? 0).toFixed(1)}\u202Fms`);
  return <MotionFps />;
}

/** The HUD corner: engine caption, scale bar and gnomon, bottom-aligned at the right. */
export function Hud() {
  return (
    <div className="hud" data-inset="bottom-right">
      <EngineStatus />
      <ScaleBar />
      <Orientation />
    </div>
  );
}
